import {
  X,
  User,
  Star,
  Calendar,
  MessageSquare,
  CheckCircle,
  Edit,
  Trash2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { Feedback } from '../../types';
import {
  getFeedbackTypeColor, getFeedbackStatusColor 
} from '../../types';

interface FeedbackDetailModalProps {
  readonly feedback: Feedback | null;
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onEdit: (feedback: Feedback) => void;
  readonly onDelete: (feedback: Feedback) => void;
  readonly onAcknowledge: (feedback: Feedback) => void;
}

const STAR_POSITIONS = ['first', 'second', 'third', 'fourth', 'fifth'] as const;

export function FeedbackDetailModal({
  feedback,
  isOpen,
  onClose,
  onEdit,
  onDelete,
  onAcknowledge,
}: FeedbackDetailModalProps) {
  if (!isOpen || !feedback) return null;

  const typeColor = getFeedbackTypeColor(feedback.type);
  const statusColor = getFeedbackStatusColor(feedback.status);
  const formattedDate = new Date(feedback.createdAt).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
  const acknowledgedDate = feedback.acknowledgedAt
    ? new Date(feedback.acknowledgedAt).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    })
    : null;

  const canAcknowledge = feedback.status === 'submitted';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <button
        type="button"
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        onKeyDown={(e) => e.key === 'Escape' && onClose()}
        aria-label="Close modal"
      />
      <div className="relative z-10 w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-lg bg-white shadow-xl">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-white px-6 py-4">
          <h2 className="text-lg font-semibold">Feedback Details</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-1 hover:bg-gray-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="rounded-full bg-primary/10 p-3">
                <User className="h-8 w-8 text-primary" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900">{feedback.employeeName}</h3>
                <p className="text-gray-500">{feedback.employeeRole}</p>
                <p className="text-sm text-gray-400">{feedback.siteName}</p>
              </div>
            </div>
            <div className="flex flex-col items-end gap-2">
              <span className={`rounded-full px-3 py-1 text-sm font-medium capitalize ${typeColor}`}>
                {feedback.type}
              </span>
              <span className={`rounded-full px-3 py-1 text-sm font-medium capitalize ${statusColor}`}>
                {feedback.status}
              </span>
            </div>
          </div>

          <div className="border-t pt-4">
            <h4 className="text-lg font-semibold text-gray-900">{feedback.title}</h4>
            <p className="mt-2 text-gray-600 whitespace-pre-wrap">{feedback.content}</p>
          </div>

          {feedback.rating !== undefined && (
            <div className="border-t pt-4">
              <p className="text-sm font-medium text-gray-500 mb-2">Rating</p>
              <div className="flex items-center gap-1">
                {STAR_POSITIONS.map((position, starIndex) => (
                  <Star
                    key={`star-detail-${feedback.feedbackId}-${position}`}
                    className={`h-6 w-6 ${
                      starIndex < (feedback.rating ?? 0) ? 'fill-amber-400 text-amber-400' : 'text-gray-300'
                    }`}
                  />
                ))}
                <span className="ml-2 text-lg font-medium text-gray-700">
                  {feedback.rating}/5
                </span>
              </div>
            </div>
          )}

          <div className="border-t pt-4 grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-gray-500 mb-1">Category</p>
              <div className="flex items-center gap-2">
                <MessageSquare className="h-4 w-4 text-gray-400" />
                <span className="text-gray-900">{feedback.category}</span>
              </div>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500 mb-1">Submitted</p>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-gray-400" />
                <span className="text-gray-900">{formattedDate}</span>
              </div>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500 mb-1">Submitted By</p>
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-gray-400" />
                <span className="text-gray-900">{feedback.submittedByName}</span>
              </div>
            </div>
            {acknowledgedDate && (
              <div>
                <p className="text-sm font-medium text-gray-500 mb-1">Acknowledged</p>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span className="text-gray-900">{acknowledgedDate}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="sticky bottom-0 flex items-center justify-between border-t bg-gray-50 px-6 py-4">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onEdit(feedback)}
            >
              <Edit className="mr-2 h-4 w-4" />
              Edit
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="text-red-600 hover:bg-red-50 hover:text-red-700"
              onClick={() => onDelete(feedback)}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </Button>
          </div>
          <div className="flex items-center gap-2">
            {canAcknowledge && (
              <Button
                size="sm"
                onClick={() => onAcknowledge(feedback)}
              >
                <CheckCircle className="mr-2 h-4 w-4" />
                Acknowledge
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
